import type { QuotationRecurringTypeValue } from './quotationFormConstants';
import { quotationDatetimeLocalFromIso } from './quotationFormDateTime';
import type { QuotationFormGroupRow } from './quotationFormGroups';

/** Interval (bulan) yang diisi otomatis dan dikunci saat jenis perulangan Triwulan. */
export const QUOTATION_TRIWULAN_RECURRING_INTERVAL = 6;

export type RecurringType = QuotationRecurringTypeValue;
export type LegacyContractPeriodType = 'week' | 'month' | 'year';

export type QuotationContractHeaderInput = {
  contract_period_start_date: string;
  contract_period_end_date: string;
};

export type QuotationGroupRecurringInput = {
  recurring_type: RecurringType | '';
  recurring_interval: string;
  recurring_count_override: string;
  recurring_start_date: string;
};

export type SyncRecurringEventsInput = {
  recurring_type: RecurringType;
  recurring_interval: number;
  recurring_count_override: number | null;
  recurring_start_date: string | null;
  contract_period_start_date: string;
  contract_period_end_date: string;
};

function parseDate(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Legacy: exclusive end from long + type (used when reading old quotations). */
function legacyContractExclusiveEnd(
  start: Date,
  periodLong: number,
  periodType: LegacyContractPeriodType,
): Date {
  const out = new Date(start.getTime());
  const n = Math.max(1, periodLong);
  if (periodType === 'week') out.setDate(out.getDate() + n * 7);
  else if (periodType === 'month') out.setMonth(out.getMonth() + n);
  else out.setFullYear(out.getFullYear() + n);
  return out;
}

/** Inclusive last day/time of contract from legacy long+type fields. */
export function legacyContractInclusiveEndDate(
  start: Date,
  periodLong: number,
  periodType: LegacyContractPeriodType,
): Date {
  const exclusive = legacyContractExclusiveEnd(start, periodLong, periodType);
  const inclusive = new Date(exclusive.getTime());
  inclusive.setDate(inclusive.getDate() - 1);
  return inclusive;
}

export function quotationContractEndDateLocalFromSources(opts: {
  contract_period_end_date?: string | null;
  contract_period_start_date?: string | null;
  contract_period_long?: number | null;
  contract_period_type?: LegacyContractPeriodType | null;
}): string {
  if (opts.contract_period_end_date) {
    return quotationDatetimeLocalFromIso(opts.contract_period_end_date);
  }
  const startIso = opts.contract_period_start_date;
  const long = opts.contract_period_long;
  const type = opts.contract_period_type;
  if (startIso && long != null && long >= 1 && type) {
    const start = new Date(startIso);
    if (!Number.isNaN(start.getTime())) {
      return quotationDatetimeLocalFromIso(
        legacyContractInclusiveEndDate(start, long, type).toISOString(),
      );
    }
  }
  return '';
}

function addRecurringStep(d: Date, type: RecurringType, interval: number): Date {
  const out = new Date(d.getTime());
  const step = Math.max(1, interval);
  if (type === 'weekly') out.setDate(out.getDate() + step * 7);
  else if (type === 'monthly') out.setMonth(out.getMonth() + step);
  else out.setFullYear(out.getFullYear() + step);
  return out;
}

export function computeRecurringEventDates(input: SyncRecurringEventsInput): Date[] {
  const headerStart = parseDate(input.contract_period_start_date);
  const endInclusive = parseDate(input.contract_period_end_date);
  if (!headerStart || !endInclusive) return [];

  const groupStartRaw = input.recurring_start_date?.trim()
    ? parseDate(input.recurring_start_date)
    : null;
  const start = groupStartRaw ?? headerStart;

  const interval = Math.max(1, input.recurring_interval || 1);
  const type = input.recurring_type;

  if (input.recurring_count_override != null && input.recurring_count_override >= 1) {
    const dates: Date[] = [];
    let current = new Date(start.getTime());
    for (let i = 0; i < input.recurring_count_override; i++) {
      dates.push(new Date(current.getTime()));
      current = addRecurringStep(current, type, interval);
    }
    return dates;
  }

  const dates: Date[] = [];
  let current = new Date(start.getTime());
  if (current.getTime() > endInclusive.getTime()) {
    dates.push(new Date(current.getTime()));
    return dates;
  }

  while (current.getTime() <= endInclusive.getTime()) {
    dates.push(new Date(current.getTime()));
    const next = addRecurringStep(current, type, interval);
    if (next.getTime() <= current.getTime()) break;
    current = next;
  }

  return dates.length ? dates : [new Date(start.getTime())];
}

export function totalRecurringCount(input: SyncRecurringEventsInput): number {
  return computeRecurringEventDates(input).length;
}

export function parseContractHeaderInput(
  header: QuotationContractHeaderInput,
): Omit<SyncRecurringEventsInput, keyof QuotationGroupRecurringInput | 'recurring_type'> | null {
  const startRaw = header.contract_period_start_date.trim();
  const endRaw = header.contract_period_end_date.trim();
  if (!startRaw || !endRaw) return null;
  const start = new Date(startRaw);
  const end = new Date(endRaw);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  if (end.getTime() <= start.getTime()) return null;
  return {
    contract_period_start_date: start.toISOString(),
    contract_period_end_date: end.toISOString(),
  };
}

export function buildGroupRecurringInput(
  group: Pick<
    QuotationFormGroupRow,
    'recurring_type' | 'recurring_interval' | 'recurring_count_override' | 'recurring_start_date'
  >,
  header: QuotationContractHeaderInput,
): SyncRecurringEventsInput | null {
  const rawType = group.recurring_type?.trim();
  const rt = rawType === 'triwulan' ? 'monthly' : rawType;
  if (rt !== 'weekly' && rt !== 'monthly' && rt !== 'yearly') return null;
  const contract = parseContractHeaderInput(header);
  if (!contract) return null;

  const intervalRaw = group.recurring_interval.trim();
  const intervalParsed = intervalRaw ? Number.parseInt(intervalRaw, 10) : 1;
  const interval =
    rawType === 'triwulan'
      ? QUOTATION_TRIWULAN_RECURRING_INTERVAL
      : Number.isFinite(intervalParsed) && intervalParsed >= 1
        ? intervalParsed
        : 1;

  const overrideRaw = group.recurring_count_override.trim();
  const overrideParsed = overrideRaw ? Number.parseInt(overrideRaw, 10) : null;
  const override =
    overrideParsed != null && Number.isFinite(overrideParsed) && overrideParsed >= 1
      ? overrideParsed
      : null;

  const startLocal = group.recurring_start_date.trim();
  const recurring_start_date = startLocal ? new Date(startLocal).toISOString() : null;

  return {
    recurring_type: rt,
    recurring_interval: interval,
    recurring_count_override: override,
    recurring_start_date,
    ...contract,
  };
}

export function quotationFormGroupTotalRecurringCount(
  group: Pick<
    QuotationFormGroupRow,
    'recurring_type' | 'recurring_interval' | 'recurring_count_override' | 'recurring_start_date'
  >,
  header: QuotationContractHeaderInput,
): number | null {
  const input = buildGroupRecurringInput(group, header);
  if (!input) return null;
  return totalRecurringCount(input);
}

export function quotationFormGroupContractEndDate(header: QuotationContractHeaderInput): Date | null {
  const contract = parseContractHeaderInput(header);
  if (!contract) return null;
  return parseDate(contract.contract_period_end_date);
}

export function formatQuotationRecurringDate(d: Date): string {
  return d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function recurringIntervalLabel(type: RecurringType, interval: number): string {
  const n = Math.max(1, interval);
  if (type === 'weekly') return n === 1 ? 'mingguan' : `setiap ${n} minggu`;
  if (type === 'monthly') return n === 1 ? 'bulanan' : `setiap ${n} bulan`;
  return n === 1 ? 'tahunan' : `setiap ${n} tahun`;
}
